<script setup lang="ts">
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PasswordRequirements from '@/Components/PasswordRequirements.vue';
import PhoneNumber from '@/Components/PhoneNumber.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import { useForm } from '@inertiajs/vue3';
import { ref } from 'vue';

const passwordIsValid = ref<boolean>(false);
const props = defineProps({
  email: String,
});

const form = useForm({
  name: '',
  email: (props.email ?? '') as string,
  phone: {
    country_code: 'gb',
    number: '',
    numberPrefix: '+44',
  },
  password: '',
  password_confirmation: '',
  terms: false,
});

const handlePasswordValidation = (value: boolean) =>
  (passwordIsValid.value = value);

const submit = () => {
  form.post(route('register'), {
    onFinish: () => form.reset('password', 'password_confirmation'),
  });
};
</script>

<template>
  <form @submit.prevent="submit">
    <div>
      <InputLabel for="name" value="Name" />

      <TextInput
        id="name"
        v-model="form.name"
        type="text"
        class="mt-1 block w-full"
        required
        autofocus
        autocomplete="name"
      />

      <InputError class="mt-2" :message="form.errors.name" />
    </div>

    <div class="mt-4">
      <InputLabel for="email" value="Email" />

      <TextInput
        id="email"
        v-model="form.email"
        type="email"
        class="mt-1 block w-full"
        required
        autocomplete="username"
      />

      <InputError class="mt-2" :message="form.errors.email" />
    </div>

    <div class="mt-4">
      <input-label for="phone" value="Mobile (for two-factor authentication)" />

      <PhoneNumber id="phone" v-model="form.phone" class="mt-1 block w-full" />

      <InputError class="mt-2" :message="form.errors.phone" />
    </div>

    <div class="mt-4">
      <InputLabel for="password" value="Password" />

      <TextInput
        id="password"
        v-model="form.password"
        type="password"
        class="mt-1 block w-full"
        required
        autocomplete="new-password"
      />

      <InputError class="mt-2" :message="form.errors.password" />
    </div>

    <div class="mt-4">
      <InputLabel for="password_confirmation" value="Confirm Password" />

      <TextInput
        id="password_confirmation"
        v-model="form.password_confirmation"
        type="password"
        class="mt-1 block w-full"
        required
        autocomplete="new-password"
      />

      <InputError class="mt-2" :message="form.errors.password_confirmation" />
    </div>

    <PasswordRequirements
      class="mt-2"
      :password="form.password"
      :password-confirmation="form.password_confirmation"
      @password-validated="handlePasswordValidation"
    />

    <p class="mt-4 text-sm">
      By continuing, you're agreeing to our
      <a
        target="__blank"
        href="https://www.zerokey.tech/legal"
        class="text-blue-600 hover:underline"
        >Terms of Business</a
      >
      and
      <a
        target="__blank"
        href="https://www.zerokey.tech/legal"
        class="text-blue-600 hover:underline"
        >Data Processing Agreement</a
      >. We handle your personal data as described in our
      <a
        target="__blank"
        href="https://www.zerokey.tech/legal"
        class="text-blue-600 hover:underline"
        >Data Privacy Notice</a
      >.
    </p>

    <div class="flex items-center justify-end mt-6">
      <PrimaryButton
        class="ml-4"
        :class="{ 'opacity-25': form.processing }"
        :disabled="form.processing || !passwordIsValid"
      >
        Continue
      </PrimaryButton>
    </div>
  </form>
</template>
